import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Upload,
  FileSpreadsheet,
  FileText,
  Loader2,
} from "lucide-react";
import { useData } from "../context/DataContext";
import { formatKES, formatDate } from "../utils/format";

const BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

export default function Statement() {
  const [params] = useSearchParams();
  const { properties } = useData();

  const startDate = params.get("startDate") || "";
  const endDate = params.get("endDate") || "";
  const propertyId = params.get("propertyId") || "all";

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState("");
  const [exportError, setExportError] = useState("");

  const propertyName = useMemo(() => {
    if (propertyId === "all") return "All properties";
    return properties.find((p) => p.id === propertyId)?.name || "Property";
  }, [propertyId, properties]);

  useEffect(() => {
    let cancelled = false;

    if (!startDate || !endDate || startDate > endDate) {
      setError("Missing or invalid date range.");
      setLoading(false);
      return;
    }

    async function run() {
      setLoading(true);
      setError("");
      try {
        const qs = new URLSearchParams({
          startDate,
          endDate,
          propertyId,
        }).toString();
        const res = await fetch(`${BASE}/reports/payments?${qs}`, {
          credentials: "include",
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "Couldn't load statement");
        if (!cancelled) setResult(data);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Couldn't load statement");
          setResult(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, propertyId]);

  const handleExport = async (format) => {
    if (!result) return;
    setExporting(format);
    setExportError("");
    try {
      const qs = new URLSearchParams({
        startDate: result.startDate,
        endDate: result.endDate,
        propertyId: result.propertyId || "all",
        format,
      }).toString();
      const res = await fetch(
        `${BASE}/reports/payments/export?${qs}`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename =
        (match && match[1]) ||
        `paysafi-statement-${result.startDate}-to-${result.endDate}.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(e.message || "Export failed");
    } finally {
      setExporting("");
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Link
          to="/app/reports"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to reports
        </Link>
      </div>

      <div>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Statement
        </h2>
        {startDate && endDate && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {propertyName} · {formatDate(startDate)} → {formatDate(endDate)}
          </p>
        )}
      </div>

      {loading ? (
        <div className="card flex items-center justify-center gap-3 p-12 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading statement…
        </div>
      ) : error ? (
        <div className="card p-8 text-center">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          <Link
            to="/app/reports"
            className="mt-4 inline-block text-sm font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
          >
            ← Change your filters
          </Link>
        </div>
      ) : (
        result && (
          <StatementPreview
            result={result}
            propertyName={propertyName}
            exporting={exporting}
            exportError={exportError}
            onExport={handleExport}
          />
        )
      )}
    </div>
  );
}

function StatementPreview({
  result,
  propertyName,
  onExport,
  exporting,
  exportError,
}) {
  const { payments, total, count, startDate, endDate } = result;

  return (
    <div className="space-y-4">
      <div className="card p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {propertyName}
            </div>
            <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
              {formatDate(startDate)} → {formatDate(endDate)}
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {count} payment{count === 1 ? "" : "s"} · total{" "}
              <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                {formatKES(total)}
              </span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <ExportButton
              icon={FileSpreadsheet}
              label="Download Excel"
              busy={exporting === "xlsx"}
              onClick={() => onExport("xlsx")}
            />
            <ExportButton
              icon={FileText}
              label="Download PDF"
              busy={exporting === "pdf"}
              onClick={() => onExport("pdf")}
            />
          </div>
        </div>
        {exportError && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
            {exportError}
          </div>
        )}
      </div>

      {count === 0 ? (
        <div className="card p-10 text-center text-sm text-gray-500 dark:text-gray-400">
          No payments recorded between {formatDate(startDate)} and{" "}
          {formatDate(endDate)}.
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="max-h-[70vh] overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900">
                <tr className="text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Tenant</th>
                  <th className="px-4 py-3 font-medium">House</th>
                  <th className="px-4 py-3 font-medium">Property</th>
                  <th className="px-4 py-3 font-medium">Charge</th>
                  <th className="px-4 py-3 font-medium">Reference</th>
                  <th className="px-4 py-3 font-medium">Note</th>
                  <th className="px-4 py-3 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {payments.map((p) => (
                  <tr
                    key={p.id}
                    className="text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800/40"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-gray-600 dark:text-gray-400">
                      {formatDate(p.paidOn)}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      {p.tenantName || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {p.houseNumber || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {p.propertyName || "—"}
                    </td>
                    <td className="px-4 py-3 capitalize">
                      {p.chargeType || "rent"}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500 dark:text-gray-400">
                      {p.reference || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {p.note || "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-indigo-600 dark:text-indigo-400">
                      {formatKES(p.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 bg-white dark:bg-gray-900">
                <tr className="border-t border-gray-200 dark:border-gray-700">
                  <td
                    className="px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300"
                    colSpan={7}
                  >
                    Total
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-base font-bold text-indigo-600 dark:text-indigo-400">
                    {formatKES(total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ExportButton({ icon: Icon, label, onClick, busy }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-indigo-400 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-indigo-400 dark:hover:text-indigo-300"
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Icon className="h-4 w-4" />
      )}
      {label}
      <Upload className="h-3.5 w-3.5 opacity-60" />
    </button>
  );
}
